// src/slices/moduleManagement/reducer.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ModuleItem, ModuleModalState, PermissionItem } from 'pages/ModuleManagement/types';
import {
  fetchModules,
  fetchModuleDetail,
  createNewModule,
  updateExistingModule,
  deleteModuleById,
  fetchModulePermissions,
  addPermissionToModule,
  removePermission,
} from './thunk';

interface ModuleManagementState {
  modules: ModuleItem[];
  selectedModule: ModuleItem | null;
  loading: boolean;
  submitting: boolean;
  error: string | null;
  modalState: ModuleModalState;
  // Permissions của module đang mở ở tab "Tạo Permission" (form sửa)
  modulePermissions: PermissionItem[];
  modulePermissionsLoading: boolean;
  addingPermission: boolean;
  deletingPermission: boolean;
}

const initialState: ModuleManagementState = {
  modules: [],
  selectedModule: null,
  loading: false,
  submitting: false,
  error: null,
  modalState: {
    create: false,
    edit: false,
    delete: false,
  },
  modulePermissions: [],
  modulePermissionsLoading: false,
  addingPermission: false,
  deletingPermission: false,
};

const moduleManagementSlice = createSlice({
  name: 'ModuleManagement',
  initialState,
  reducers: {
    setSelectedModule: (state, action: PayloadAction<ModuleItem | null>) => {
      state.selectedModule = action.payload;
    },
    clearModulePermissions: (state) => {
      state.modulePermissions = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    openModal: (state, action: PayloadAction<keyof ModuleModalState>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof ModuleModalState>) => {
      state.modalState[action.payload] = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Danh sách modules
      .addCase(fetchModules.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchModules.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.modules = payload.items || payload.data || payload || [];
      })
      .addCase(fetchModules.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Chi tiết module (dùng để mở form edit)
      .addCase(fetchModuleDetail.fulfilled, (state, action) => {
        state.selectedModule = action.payload as unknown as ModuleItem;
      })

      // Tạo mới
      .addCase(createNewModule.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createNewModule.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.create = false;
      })
      .addCase(createNewModule.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Cập nhật
      .addCase(updateExistingModule.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(updateExistingModule.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.edit = false;
        state.selectedModule = null;
      })
      .addCase(updateExistingModule.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Xóa
      .addCase(deleteModuleById.pending, (state) => {
        state.submitting = true;
      })
      .addCase(deleteModuleById.fulfilled, (state, action) => {
        state.submitting = false;
        state.modules = state.modules.filter(m => m.id !== action.payload);
        state.modalState.delete = false;
        state.selectedModule = null;
      })
      .addCase(deleteModuleById.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Danh sách permission của module (tab "Tạo Permission" ở form sửa)
      .addCase(fetchModulePermissions.pending, (state) => {
        state.modulePermissionsLoading = true;
      })
      .addCase(fetchModulePermissions.fulfilled, (state, action) => {
        state.modulePermissionsLoading = false;
        const payload = action.payload as any;
        state.modulePermissions = payload.items || payload.data || payload || [];
      })
      .addCase(fetchModulePermissions.rejected, (state, action) => {
        state.modulePermissionsLoading = false;
        state.error = action.payload as string;
      })

      // Thêm permission mới cho module đã tồn tại
      .addCase(addPermissionToModule.pending, (state) => {
        state.addingPermission = true;
      })
      .addCase(addPermissionToModule.fulfilled, (state, action) => {
        state.addingPermission = false;
        const newPermission = action.payload as unknown as PermissionItem;
        if (newPermission && typeof newPermission === 'object' && 'id' in newPermission) {
          state.modulePermissions.push(newPermission);
        }
        if (state.selectedModule) {
          state.selectedModule.permissionCount += 1;
        }
      })
      .addCase(addPermissionToModule.rejected, (state, action) => {
        state.addingPermission = false;
        state.error = action.payload as string;
      })

      // Xóa permission
      .addCase(removePermission.pending, (state) => {
        state.deletingPermission = true;
      })
      .addCase(removePermission.fulfilled, (state, action) => {
        state.deletingPermission = false;
        state.modulePermissions = state.modulePermissions.filter(p => p.id !== action.payload);
        if (state.selectedModule && state.selectedModule.permissionCount > 0) {
          state.selectedModule.permissionCount -= 1;
        }
      })
      .addCase(removePermission.rejected, (state, action) => {
        state.deletingPermission = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedModule,
  clearModulePermissions,
  clearError,
  openModal,
  closeModal,
} = moduleManagementSlice.actions;

export default moduleManagementSlice.reducer;
